/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { IncrementalQuantity, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IGhBuyboxData } from './gh-buybox.data';
import { IGhBuyboxProps as IBuyboxExtentionProps, IGhBuyboxResources as IBuyboxExtentionResources } from './gh-buybox.props.autogenerated';

type IGhBuyboxViewProps = IBuyboxViewProps & IBuyboxExtentionProps<IGhBuyboxData>;

const BuyboxView: React.FC<IGhBuyboxViewProps> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        description,
        quantity,
        price,
        title,
        rating,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        quantityLimitsMessages,
        min,
        max,
        applyDefaultOrderSettings,
        shopSimilarDescription,
        unitOfMeasure
    } = props;

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {price}
                {unitOfMeasure}
                {props.bulkPurchaseLink}
                {description}
                {_renderTextBlock(props.slots.textBlocks)}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {quantity &&
                    _renderQuantity(
                        quantity,
                        callbacks,
                        props,
                        state,
                        resources,
                        quantityLimitsMessages,
                        min,
                        max,
                        applyDefaultOrderSettings,
                        telemetryContent
                    )}
                {_renderCartAndActions(addToCart, addToOrderTemplate, addToWishlist)}
                {inventoryLabel}
                {_renderProductSpecifications(props)}
                {_renderSizeGuide(props)}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
            </Node>
        </Module>
    );
};

const _renderProductSpecifications = (props: IGhBuyboxViewProps): JSX.Element | undefined => {
    const { config, data } = props;
    if (!config?.displayProductSpecifications) {
        return undefined;
    }

    const attributes = data?.productSpecificationData?.result;
    if (!attributes || attributes.length === 0) {
        return undefined;
    }

    const hiddenValues = _parseHiddenAttributeValues(config?.hiddenAttributeValues);
    const hideValues = config?.hideAttributeValues ?? false;

    const rows = attributes
        .map(attr => {
            if (hideValues && _shouldHideAttributeValue(attr, hiddenValues)) {
                return null;
            }
            const value = _renderAttributeValue(attr);
            if (!value || !attr.Name) {
                return null;
            }
            return (
                <div className='gh-buybox__spec-row' key={attr.RecordId ?? attr.Name}>
                    <span className='gh-buybox__spec-label'>{attr.Name}</span>
                    <span className='gh-buybox__spec-value'>{value}</span>
                </div>
            );
        })
        .filter((row): row is JSX.Element => row !== null);

    if (rows.length === 0) {
        return undefined;
    }

    const heading = config?.productSpecificationsHeading || 'Produktinformation';

    return (
        <details className='gh-buybox__details-item'>
            <summary className='gh-buybox__details-summary'>{heading}</summary>
            <div className='gh-buybox__details-body gh-buybox__spec-list'>{rows}</div>
        </details>
    );
};

const _parseHiddenAttributeValues = (raw: string | undefined): string[] => {
    if (!raw) {
        return [];
    }
    return raw
        .split(';')
        .map(value => value.trim().toLowerCase())
        .filter(value => value.length > 0);
};

// Mirrors gh-product-specification's _shouldHideAttributeValue: hides null/empty
// values and any value matching the case-insensitive hidden-values list.
const _shouldHideAttributeValue = (attr: AttributeValue, hiddenValues: string[]): boolean => {
    let valueToCheck: string | null = null;
    switch (attr.DataTypeValue) {
        case 1:
            valueToCheck = attr.CurrencyValue?.toString() ?? null;
            break;
        case 2:
            valueToCheck = attr.DateTimeOffsetValue?.toUTCString() ?? null;
            break;
        case 3:
            valueToCheck = attr.FloatValue?.toString() ?? null;
            break;
        case 4:
            valueToCheck = attr.IntegerValue?.toString() ?? null;
            break;
        case 5:
            valueToCheck = attr.TextValue ?? null;
            break;
        case 6:
            valueToCheck = attr.BooleanValue?.toString() ?? null;
            break;
        default:
            return false;
    }

    if (!valueToCheck) {
        return true;
    }
    const trimmed = valueToCheck.trim();
    if (trimmed === '') {
        return true;
    }
    return hiddenValues.includes(trimmed.toLowerCase());
};

const _renderAttributeValue = (attr: AttributeValue): React.ReactNode => {
    switch (attr.DataTypeValue) {
        case 1:
            return attr.CurrencyValue !== null && attr.CurrencyValue !== undefined
                ? `${attr.CurrencyCode ?? ''} ${attr.CurrencyValue}`.trim()
                : null;
        case 2:
            return attr.DateTimeOffsetValue ? attr.DateTimeOffsetValue.toLocaleDateString() : null;
        case 3:
            return attr.FloatValue !== null && attr.FloatValue !== undefined ? attr.FloatValue : null;
        case 4:
            return attr.IntegerValue !== null && attr.IntegerValue !== undefined ? attr.IntegerValue : null;
        case 5:
            return attr.TextValue ? attr.TextValue : null;
        case 6:
            return attr.BooleanValue ? 'Ja' : 'Nej';
        default:
            return null;
    }
};

const _renderSizeGuide = (props: IGhBuyboxViewProps): JSX.Element | undefined => {
    const { config } = props;
    const link = config?.sizeGuideLink;
    const href = link?.linkUrl?.destinationUrl;
    if (!href) {
        return undefined;
    }

    const heading = config?.sizeGuideHeading || 'Storleksguide';
    const intro = config?.sizeGuideText;
    const linkText = link?.linkText || heading;
    const target = link?.openInNewTab ? '_blank' : undefined;
    const rel = link?.openInNewTab ? 'noopener noreferrer' : undefined;

    return (
        <details className='gh-buybox__details-item'>
            <summary className='gh-buybox__details-summary'>{heading}</summary>
            <div className='gh-buybox__details-body'>
                {intro && <p className='gh-buybox__details-text'>{intro}</p>}
                <a className='gh-buybox__details-link' href={href} target={target} rel={rel} aria-label={link?.ariaLabel}>
                    {linkText}
                </a>
            </div>
        </details>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return <>{textBlocks[0]}</>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps
): JSX.Element | undefined => {
    if (!addToCart && !addToWishlist) {
        return undefined;
    }

    return (
        <div className='product-add-to-cart'>
            {addToCart && addToCart.errorBlock}
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            {addToWishlist && addToWishlist.errorBlock}
            <div className='buttons'>
                {addToCart && addToCart.button}
                {addToOrderTemplate && addToOrderTemplate.button}
                {addToWishlist && addToWishlist.button}
            </div>
        </div>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,
    min: number | undefined,
    max: number | undefined,
    applyDefaultOrderSettings?: boolean,
    telemetryContent?: ITelemetryContent
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                key={`quantity-${quantity}`}
                min={min}
                max={max}
                applyDefaultOrderSettings={applyDefaultOrderSettings}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            <Node {...ContainerProps}>{errors}</Node>
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

export default BuyboxView;
